import Activity from '@model/Activity'
import Locale from '@serv/Locale'
import Schedule from '@model/Schedule'
import store from '@/store'

// For non-patient logins, these are the only keyValues.stringMap key substrings that should be poked into our global
// Locale.stringMap
const stringMapGlobalKeySubstrings = ['appointmentType', 'rom']

/**
 * A Journey object.
 */
class Journey {
    constructor(object) {
        this.id = object.id
        this.isLegacy = object.isLegacy
        this.keyValues = object.keyValues || {}
        this.procedureCode = object.procedureCode
        this.defaultLos = object.defaultLos || 0 // Default length of stay, in nights

        // These can come in as tuples or as a string
        this.procedureLabels = (object.procedureLabels || []).map(label =>
            (typeof label == 'string' ? label : label[0])
        )

        this.providerSlugs = object.providerSlugs

        this.slug = object.slug
        this.jointSlug = object.jointSlug || object.joint
        this.isBilateral = object.isBilateral

        // TEMP to handle null title
        if (object.title) {
            this.titleRaw = object.title
        } else if (object.procedureCode) {
            this.titleRaw = `Unknown (${object.procedureCode})`
        } else {
            this.titleRaw = 'Unknown'
        }

        this.zones = object.zones != undefined ? object.zones : [object.zone]

        // Construct Activity objects
        const objects = object.activities || []
        this.activitiesMap = {}
        objects.forEach(object => {
            // If the activity scheduleSlug can't be understood - for example, the milestone abbreviation relates
            // to a content bundle milestone - then filter it out
            const schedule = Schedule.get(object.scheduleSlug)
            if (schedule != undefined) {
                const activity = new Activity(object)
                this.activitiesMap[activity.slug] = activity
            } /* else {
                Logging.warn(
                    `Could not create schedule from slug: ${object.scheduleSlug}`
                )
            }*/
        })

        // this.keyValues.patientPageTabs will be merged with any Owner.keyValues.dash.patientPageTabs and cached here
        this.patientPageTabs = undefined

        this.rtmStartSchedule = object.rtmStartSchedule
        this.rtmEndSchedule = object.rtmEndSchedule
        this.rtmReminderSchedules = object.rtmReminderSchedules || []
    }

    // Get an array of activity objects.
    get activities() {
        return Object.values(this.activitiesMap)
    }

    // Get an array of activity slugs.
    get activitySlugs() {
        return Object.keys(this.activitiesMap)
    }

    /**
     * Called from resources.setJourneys(), after initial login (also from mockJourneys).
     */
    postProcess() {
        if (store.state.user.user?.isPatient) {
            this.patchNotAppSurveyActivities()
        }
    }

    patchNotAppSurveyActivities() {
        const contentMap = store.state.content.content

        this.activities.forEach(activity => {
            const content = contentMap[activity.contentSlug]

            if (content?.tags?.includes('not-app-survey')) {
                delete this.activitiesMap[activity.slug]
            }
        })
    }

    /**
     * Get journey title.
     * If legacy, add prefix.
     */
    get title() {
        return this.isLegacy ? Locale.getLanguageItem('patientJourneyLegacyTitle', [this.titleRaw]) : this.titleRaw
    }

    get titleLocalised() {
        return this.title
    }

    /**
     * Get journey title, to the right of initial : symbol.
     * If legacy, add prefix.
     */
    get titleShortened() {
        const title = this.titleRaw.search(':') < 0 ? this.titleRaw : this.titleRaw.split(':')[1].trim()

        return this.isLegacy ? Locale.getLanguageItem('patientJourneyLegacyTitle', [title]) : title
    }

    /**
     * Check is RTM period set for the journey
     */
    get hasRtm() {
        return this.rtmStartSchedule && this.rtmEndSchedule
    }

    /**
     * Return true if the journey slug matches the specified mask.
     */
    matchesSlugMask(mask) {
        return this.slug.includes(mask) || mask == '*'
    }

    // Apply certain keyValues.stringMap items to the global Locale.stringMap
    applyStringMapToLocaleForUser(user) {
        const stringMap = this.keyValues?.stringMap || {}
        Object.keys(stringMap).forEach(key => {
            if (user.isPatient || stringMapGlobalKeySubstrings.some(substring => key.includes(substring))) {
                Locale.stringMap[key] = stringMap[key]
            }
        })
    }
}

// Journey laterality.
Journey.Side = {
    both: 'both',
    left: 'left',
    right: 'right'
}

export default Journey
